import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const SLIDES = [
  // Kids Academy slides — shown first
  {
    bg: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?crop=entropy&cs=srgb&fm=jpg&q=85",
    eyebrow: "EduHub Kids Academy",
    title: "Fun live classes that help children learn, create, and grow.",
    cta: "Explore Kids Courses",
    ctaLink: "/courses",
    accent: "#C5A059",
  },
  {
    bg: "https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&q=85",
    eyebrow: "Build Future Skills Early",
    title: "Coding, Robotics, Mathematics, Languages, Creativity, and Digital Skills for Children.",
    cta: "Start Learning",
    ctaLink: "/courses",
    accent: "#C5A059",
  },
  // Adult course slides
  {
    bg: "https://images.unsplash.com/photo-1541178735493-479c1a27ed24?crop=entropy&cs=srgb&fm=jpg&q=85",
    eyebrow: "Semester I · 2026",
    title: "Python, ROS 2 & IELTS prep are now enrolling.",
  },
  {
    bg: "https://images.unsplash.com/photo-1603354350317-6f7aaa5911c5?crop=entropy&cs=srgb&fm=jpg&q=85",
    eyebrow: "Live · small cohorts",
    title: "Real teachers. Real laboratories. Real outcomes.",
  },
  {
    bg: "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=85&w=940&h=1175",
    eyebrow: "Robotics & Engineering",
    title: "Build the machines you used to only read about.",
  },
  {
    bg: "https://images.unsplash.com/photo-1736066330610-c102cab4e942?crop=entropy&cs=srgb&fm=jpg&q=85",
    eyebrow: "Languages · including Romanian",
    title: "Speak like a local. Taught by natives.",
  },
];

export default function HeroCarousel() {
  const [i, setI] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    ref.current = setInterval(() => setI((p) => (p + 1) % SLIDES.length), 5500);
    return () => clearInterval(ref.current);
  }, []);

  return (
    <div className="relative aspect-[4/5] overflow-hidden border border-eduhu-border bg-eduhu-bg3">
      {SLIDES.map((s, idx) => (
        <div
          key={idx}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === idx ? 1 : 0 }}
        >
          <img
            src={s.bg}
            alt={s.eyebrow}
            className="w-full h-full object-cover"
            loading={idx === 0 ? "eager" : "lazy"}
            fetchpriority={idx === 0 ? "high" : "low"}
            width="940"
            height="1175"
            style={{
              transform: i === idx ? "scale(1.04)" : "scale(1.0)",
              transition: "transform 6000ms ease-out",
            }}
          />
          <div className="absolute left-0 bottom-0 bg-eduhu-dark text-eduhu-bg p-5 max-w-[90%]">
            <div
              className="overline"
              style={{ color: s.accent || "#C5A059" }}
            >
              {s.eyebrow}
            </div>
            <div className="font-serif text-2xl leading-tight mt-1">{s.title}</div>
            {s.cta && s.ctaLink && (
              <Link
                to={s.ctaLink}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-sans uppercase tracking-widest border border-eduhu-gold text-eduhu-gold hover:bg-eduhu-gold hover:text-eduhu-dark transition-colors px-3 py-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                {s.cta} <ArrowRight size={11} strokeWidth={2} />
              </Link>
            )}
          </div>
        </div>
      ))}

      <div className="absolute right-4 top-4 flex gap-1 z-10" aria-label="Carousel slides">
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            data-testid={`carousel-dot-${idx}`}
            aria-label={`Go to slide ${idx + 1} of ${SLIDES.length}`}
            aria-current={i === idx ? "true" : undefined}
            onClick={() => setI(idx)}
            className="flex items-center justify-center min-w-[24px] min-h-[24px]"
          >
            <span
              className={`block h-1.5 transition-all duration-300 ${
                i === idx ? "w-7 bg-eduhu-gold" : "w-3 bg-white/70"
              }`}
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
